import { useEffect, useState } from 'react';
import { uc } from '../unifiedContactsApi';

export const SEARCH_DEBOUNCE_MS = 250;
export const SEARCH_LIMIT = 25;

const NONE = { key: null, items: [], total: 0, error: null };
const NO_ITEMS = [];

// Ticket 188: server search for the contact pickers. The tenant has ~47k
// contacts, so loading the first 200/500 and filtering in the browser left most
// people unpickable. This asks GET /v2/contacts?q= instead, debounced, and drops
// any response that lands after newer typing (or after unmount).
//   idle     nothing to search yet ("Type to search"): blank query, or disabled.
//            `browse` fetches the newest page for a blank query instead.
//   loading  the query changed and its answer has not landed.
//   error    the call failed (the raw error, for errorMessage()).
//   total    matches on the server, for a "showing N of M" hint.
export default function useContactSearch(query, { enabled = true, limit = SEARCH_LIMIT, browse = false } = {}) {
  const q = (query || '').trim();
  const idle = !enabled || (!q && !browse);
  const key = `${limit}|${q}`;
  const [res, setRes] = useState(NONE);

  useEffect(() => {
    if (idle) { setRes(NONE); return undefined; }
    let live = true;
    const timer = setTimeout(() => {
      uc.list(q ? { q, limit } : { limit })
        .then((r) => { if (live) setRes({ key, items: r?.items || NO_ITEMS, total: r?.total ?? 0, error: null }); })
        .catch((error) => { if (live) setRes({ key, items: NO_ITEMS, total: 0, error }); });
    }, q ? SEARCH_DEBOUNCE_MS : 0);
    return () => { live = false; clearTimeout(timer); };
  }, [idle, q, limit, key]);

  const current = !idle && res.key === key; // the answer on screen is for this query
  return {
    idle,
    loading: !idle && !current,
    items: current ? res.items : NO_ITEMS,
    total: current ? res.total : 0,
    error: current ? res.error : null,
  };
}
